/**
 * Stitch-line matching (#199). Two seams sewn together along an edge should
 * line up hole-for-hole even when they have different lengths (a flat panel vs.
 * the curve it wraps). A `StitchMatch` is **directional**: the `main` seam keeps
 * full control of its own chisel settings, and the `follower` adopts the main's
 * current hole **count**, spread evenly along its own length (so spacing differs
 * by length / curvature). Matches are 1:1 and may span two different pieces.
 *
 * The relationship is stored as a project-level `StitchMatch` record plus a
 * `matchId` back-ref on both members and a derived `holeCount` on the follower.
 * The model is pure (no Paper): `deriveStitchMatches` re-stamps the follower on
 * every commit — so editing the main's pitch re-stitches the follower — and
 * prunes a match whose member was deleted, mirroring how offset / mirror
 * connections re-derive.
 */

import {
  pieceStitchLines,
  projectStitchMatches,
  resolvePartStitchingDefaults,
  type Piece,
  type Project,
  type StitchingDefaults,
  type StitchLine,
  type StitchMatch,
  type StitchRef,
} from "./Project";
import {
  effectiveHoleCount,
  effectivePitch,
  effectiveSkipEvery,
  isStitchLoop,
} from "./stitching";
import { newId } from "./ids";

/** The visual chisel style a follower adopts from (or restores around) a match. */
type StitchStyle = NonNullable<StitchMatch["followerStyle"]>;

/** Pull the chisel style off a line. */
function styleOf(line: StitchLine): StitchStyle {
  return {
    shape: line.shape,
    size: line.size,
    slantAngle: line.slantAngle,
    pitchMode: line.pitchMode,
    overridesDefaults: line.overridesDefaults,
  };
}

/** True when a line's chisel matches its piece's resolved defaults — so a
 *  matched follower whose adopted style equals the defaults is flagged as
 *  inheriting (and buckets with the defaults in the layers panel) rather than as
 *  a phantom override. Pitch is ignored: a matched follower is count-driven. */
function styleMatchesDefaults(line: StitchLine, d: StitchingDefaults): boolean {
  return (
    line.shape === d.shape &&
    Math.abs(line.size - d.size) < 1e-6 &&
    Math.abs(line.slantAngle - d.slantAngle) < 1e-6 &&
    (line.pitchMode ?? "center") === (d.pitchMode ?? "center")
  );
}

/** Equality on just the fields `deriveStitchMatches` manages — lets it return
 *  the same line reference (and thus a memoized project) when nothing changed. */
function stitchManagedFieldsEqual(a: StitchLine, b: StitchLine): boolean {
  return (
    a.matchId === b.matchId &&
    a.holeCount === b.holeCount &&
    a.matchSpacing === b.matchSpacing &&
    a.skipMarkEvery === b.skipMarkEvery &&
    a.startOffset === b.startOffset &&
    a.fillWorkingArea === b.fillWorkingArea &&
    a.slantReverse === b.slantReverse &&
    a.shape === b.shape &&
    a.size === b.size &&
    a.slantAngle === b.slantAngle &&
    a.pitchMode === b.pitchMode &&
    a.overridesDefaults === b.overridesDefaults
  );
}

/** Write a chisel style back onto the follower line (used by Break to restore
 *  the follower's own pre-match style). */
function restoreFollowerStyle(
  project: Project,
  ref: StitchRef,
  style: StitchStyle,
): Project {
  return {
    ...project,
    pieces: project.pieces.map((pc) =>
      pc.id !== ref.pieceId
        ? pc
        : {
            ...pc,
            stitchLines: pieceStitchLines(pc).map((l) =>
              l.id !== ref.stitchId
                ? l
                : {
                    ...l,
                    shape: style.shape,
                    size: style.size,
                    slantAngle: style.slantAngle,
                    pitchMode: style.pitchMode,
                    overridesDefaults: style.overridesDefaults,
                  },
            ),
          },
    ),
  };
}

/** Locate a stitch line + its parent piece by ref, or null when gone. */
function findStitchLine(
  project: Project,
  ref: StitchRef,
): { piece: Piece; line: StitchLine } | null {
  const piece = project.pieces.find((p) => p.id === ref.pieceId);
  if (!piece) return null;
  const line = pieceStitchLines(piece).find((l) => l.id === ref.stitchId);
  return line ? { piece, line } : null;
}

/** The match a stitch line belongs to (as main or follower), or null. For a
 *  main with several followers this returns the FIRST such match; use
 *  `matchesForStitch` to enumerate them all. */
export function matchForStitch(
  project: Project,
  stitchId: string,
): StitchMatch | null {
  return (
    projectStitchMatches(project).find(
      (m) => m.main.stitchId === stitchId || m.follower.stitchId === stitchId,
    ) ?? null
  );
}

/** Every match a stitch participates in — as the main (a main may have MANY
 *  followers, #199 path B) or as a follower (a follower follows exactly one
 *  main). */
export function matchesForStitch(
  project: Project,
  stitchId: string,
): StitchMatch[] {
  return projectStitchMatches(project).filter(
    (m) => m.main.stitchId === stitchId || m.follower.stitchId === stitchId,
  );
}

/** True when `stitchId` is the MAIN (controlling) seam of `match`. */
export function isStitchMatchMain(
  match: StitchMatch,
  stitchId: string,
): boolean {
  return match.main.stitchId === stitchId;
}

/** The OTHER member of a match, given one member's stitch id. */
export function stitchMatchPartner(
  match: StitchMatch,
  stitchId: string,
): StitchRef {
  return match.main.stitchId === stitchId ? match.follower : match.main;
}

/**
 * Re-stamp every follower with its main's current hole count (+ `matchId` on
 * both members), clear those fields on every other line, and prune any match
 * whose member was deleted (the survivor is "baked" back to its own row). Pure
 * and memoized — returns the same project reference when nothing changed, so it
 * is cheap to run on every commit.
 */
export function deriveStitchMatches(project: Project): Project {
  const matches = projectStitchMatches(project);

  const lineById = new Map<string, StitchLine>();
  for (const piece of project.pieces) {
    for (const l of pieceStitchLines(piece)) lineById.set(l.id, l);
  }

  // Surviving matches (both members exist) + the per-line desired fields. The
  // main keeps its own settings (no holeCount); the follower gets the main's
  // current count AND its hole style/size, so the two seams look identical.
  const survivors: StitchMatch[] = [];
  const desired = new Map<
    string,
    {
      matchId: string;
      holeCount?: number;
      style?: StitchStyle;
      matchSpacing?: number;
      startOffset?: number;
      skipMarkEvery?: number;
    }
  >();
  for (const m of matches) {
    const mainLine = lineById.get(m.main.stitchId);
    const followerLine = lineById.get(m.follower.stitchId);
    if (!mainLine || !followerLine) continue;
    survivors.push(m);
    // A main marks every Nth hole to skip when a follower drops every Nth
    // (#687). With several followers, the first one that skips wins (v1).
    const prevMain = desired.get(m.main.stitchId);
    const followerSkip = effectiveSkipEvery(followerLine);
    desired.set(m.main.stitchId, {
      matchId: m.id,
      skipMarkEvery: prevMain?.skipMarkEvery ?? followerSkip,
    });
    desired.set(m.follower.stitchId, {
      matchId: m.id,
      holeCount: effectiveHoleCount(mainLine),
      style: styleOf(mainLine),
      // The main's center-to-center pitch, so the follower can lay holes at the
      // SAME pitch (the "Match pitch exactly" default).
      matchSpacing: effectivePitch(mainLine),
      // A continuous (closed-loop) follower adopts the main's start-of-path so
      // the two rings stay aligned hole-for-hole — moving the main's start
      // rotates the follower too (#692). Open seams ignore startOffset, so only
      // a loop main propagates it.
      startOffset: isStitchLoop(mainLine)
        ? (mainLine.startOffset ?? 0)
        : undefined,
    });
  }

  let changed = survivors.length !== matches.length;
  const pieces = project.pieces.map((piece) => {
    const lines = pieceStitchLines(piece);
    if (lines.length === 0) return piece;
    let pieceChanged = false;
    let pieceDefaults: StitchingDefaults | null = null;
    const nextLines = lines.map((l) => {
      const d = desired.get(l.id);
      // `slantReverse` + `fillWorkingArea` are the follower's OWN overrides —
      // preserve them on a follower, strip them from everyone else. `matchId` /
      // `holeCount` / `matchSpacing` are derived — re-stamped below for a
      // follower, dropped otherwise.
      const {
        matchId: _m,
        holeCount: _h,
        matchSpacing: _ms,
        skipMarkEvery: _sme,
        slantReverse: lReverse,
        fillWorkingArea: lFill,
        ...rest
      } = l;
      let next: StitchLine = { ...rest };
      if (d?.matchId != null) next.matchId = d.matchId;
      if (d?.holeCount != null) next.holeCount = d.holeCount;
      if (d?.matchSpacing != null) next.matchSpacing = d.matchSpacing;
      if (d?.skipMarkEvery != null) next.skipMarkEvery = d.skipMarkEvery;
      // A loop follower's rotation follows the main's start-of-path (#692).
      if (d?.startOffset != null) next.startOffset = d.startOffset;
      // The follower adopts the main's chisel style.
      if (d?.style) {
        next = { ...next, ...d.style };
        // …keeping its own "fill working area" opt-out, when it set one.
        if (lFill !== undefined) next.fillWorkingArea = lFill;
        // …keeping its own slant DIRECTION override, when it set one.
        if (lReverse !== undefined) {
          next.slantReverse = lReverse;
          next.slantAngle = (lReverse ? 1 : -1) * Math.abs(d.style.slantAngle);
        }
        // Flag it as an override only when the adopted style actually differs
        // from the part defaults — so a follower whose chisel equals the
        // defaults buckets WITH them in the layers panel (matching its main),
        // not as a phantom "Stitching (slant)" override group.
        if (!pieceDefaults) {
          pieceDefaults = resolvePartStitchingDefaults(project, piece);
        }
        next.overridesDefaults = !styleMatchesDefaults(next, pieceDefaults);
      }
      if (stitchManagedFieldsEqual(l, next)) return l;
      pieceChanged = true;
      return next;
    });
    if (!pieceChanged) return piece;
    changed = true;
    return { ...piece, stitchLines: nextLines };
  });

  if (!changed) return project;
  const next: Project = { ...project, pieces };
  if (survivors.length !== matches.length) {
    if (survivors.length === 0) delete next.stitchMatches;
    else next.stitchMatches = survivors;
  }
  return next;
}

/**
 * Match `follower` to `main`: the follower adopts the main's hole count.
 * Matching is 1:1, so any existing match on either line is broken first.
 * No-op for a self-match or a missing line.
 */
export function createStitchMatch(
  project: Project,
  main: StitchRef,
  follower: StitchRef,
): Project {
  if (main.stitchId === follower.stitchId) return project;
  if (!findStitchLine(project, main) || !findStitchLine(project, follower)) {
    return project;
  }
  // 1:1 — drop any prior match on either line.
  let next = project;
  for (const ref of [main, follower]) {
    const existing = matchForStitch(next, ref.stitchId);
    if (existing) next = breakStitchMatch(next, existing.id);
  }
  // Snapshot the follower's own style now (after any prior match was restored)
  // so Break can put it back; while matched the follower adopts the main's.
  const followerLine = findStitchLine(next, follower)?.line;
  const match: StitchMatch = {
    id: newId("smatch"),
    main,
    follower,
    ...(followerLine ? { followerStyle: styleOf(followerLine) } : {}),
  };
  next = { ...next, stitchMatches: [...projectStitchMatches(next), match] };
  return deriveStitchMatches(next);
}

/**
 * Match every seam in `followers` to one `main` (#199 path B, N-way): the main
 * keeps its own count + style and each follower adopts it. A main may have MANY
 * followers; a follower follows exactly ONE main. The main is first freed if it
 * was itself a follower (so it's a clean controller, not both), and each
 * follower is detached from any prior match it was in, so the result is one
 * tidy one-main-to-many group. No-op for a missing main.
 */
export function matchFollowersToMain(
  project: Project,
  main: StitchRef,
  followers: StitchRef[],
): Project {
  if (!findStitchLine(project, main)) return project;
  let next = project;
  // The main must be a pure controller — if it currently follows someone, drop
  // that match so it isn't a follower and a main at once.
  for (const m of matchesForStitch(next, main.stitchId)) {
    if (m.follower.stitchId === main.stitchId) {
      next = breakStitchMatch(next, m.id);
    }
  }
  for (const follower of followers) {
    if (follower.stitchId === main.stitchId) continue;
    if (!findStitchLine(next, follower)) continue;
    // Free the follower from every prior match (as a follower, or as its own
    // main — its old followers bake back to standalone).
    for (const m of matchesForStitch(next, follower.stitchId)) {
      next = breakStitchMatch(next, m.id);
    }
    const followerLine = findStitchLine(next, follower)?.line;
    const match: StitchMatch = {
      id: newId("smatch"),
      main,
      follower,
      ...(followerLine ? { followerStyle: styleOf(followerLine) } : {}),
    };
    next = { ...next, stitchMatches: [...projectStitchMatches(next), match] };
  }
  return deriveStitchMatches(next);
}

/** Break a match: the follower drops the main's count and gets its own chisel
 *  style back (the snapshot taken when the match was created). */
export function breakStitchMatch(project: Project, matchId: string): Project {
  const matches = projectStitchMatches(project);
  const match = matches.find((m) => m.id === matchId);
  if (!match) return project;
  const remaining = matches.filter((m) => m.id !== matchId);
  let next: Project = { ...project };
  if (remaining.length === 0) delete next.stitchMatches;
  else next.stitchMatches = remaining;
  if (match.followerStyle) {
    next = restoreFollowerStyle(next, match.follower, match.followerStyle);
  }
  return deriveStitchMatches(next);
}
